import { combineReducers, compose, createSelector } from '@reduxjs/toolkit';
import { groupBy } from 'lodash';

import slice from './photos.slice';
import photosAdapter from './photos.adapter';

import type {
  PhotoUpload,
  NamedCollection,
  UncollectedCollection,
  CollectionName,
} from './photos.types';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

// Localized Selectors
const defaultSelectors = photosAdapter.getSelectors();

const selectActiveCollection = (state: LocalState) => state.activeCollection;
const selectNewUploadsCollection = (state: LocalState) => state.newUploadsCollection;

const selectPhotosForCollection = (state: LocalState, collection?: string | null) => {
  if (collection === undefined) return [];

  return state.ids
    .map((id) => state.entities[id] as PhotoUpload)
    .filter((p) => p.collection === collection);
};

const selectAllPhotosForActiveCollection = createSelector(
  [defaultSelectors.selectAll, selectActiveCollection],
  (all, collection) => all.filter((p) => p.collection === collection)
);

const selectUncollectedPhotos = createSelector([defaultSelectors.selectAll], (phs) => {
  return phs.filter((p) => p.collection === null);
});

const selectUncollectedCollection = createSelector(
  [selectUncollectedPhotos],
  (uncollectedPhotos) => {
    return {
      name: null,
      count: uncollectedPhotos.length,
      thumb: uncollectedPhotos.length > 0 ? uncollectedPhotos[0].file_path_thumb : null,
    } as UncollectedCollection;
  }
);

const selectAllNamedCollections = createSelector([defaultSelectors.selectAll], (allPhotos) => {
  const namedCollectionPhotos = allPhotos.filter((p) => p.collection !== null);
  const photosByCollection = groupBy(namedCollectionPhotos, 'collection');
  return Object.entries(photosByCollection).map(([collection, collectionPhotos]) => {
    return {
      name: collection,
      count: collectionPhotos.length,
      thumb: collectionPhotos[0].file_path_thumb,
    } as NamedCollection;
  });
});

const selectNamedCollectionNames = createSelector([selectAllNamedCollections], (collections) => {
  return collections.map((c) => c.name);
});

// Globalized selectors
export default {
  ...photosAdapter.getSelectors(localState),
  activeCollection: compose(selectActiveCollection, localState),
  allForActiveCollection: compose(selectAllPhotosForActiveCollection, localState),
  allForCollection: (state: GlobalState, collection?: CollectionName) =>
    selectPhotosForCollection(localState(state), collection),
  allCollectionNames: compose(selectNamedCollectionNames, localState),
  allNamedCollections: compose(selectAllNamedCollections, localState),
  allUncollected: compose(selectUncollectedPhotos, localState),
  newUploadsCollection: compose(selectNewUploadsCollection, localState),
  uncollectedCollection: compose(selectUncollectedCollection, localState),
};
